import { useState } from "react";
import { ChevronDown, Pencil } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

type ProfileMobileHeaderProps = {
  displayName: string;
  joinDateText: string;
  bioText: string;
  avatarUrl: string;
  nameInitial: string;
  completedWorkouts: number;
  totalPoints: number;
  metricsLoading?: boolean;
  onEditProfile: () => void;
  onManageSubscription: () => void;
  /** When true, renders inside the gradient page header (no extra chrome). */
  embeddedInHero?: boolean;
  className?: string;
};

export function ProfileMobileHeader({
  displayName,
  joinDateText,
  bioText,
  avatarUrl,
  nameInitial,
  completedWorkouts,
  totalPoints,
  metricsLoading = false,
  onEditProfile,
  onManageSubscription,
  embeddedInHero = false,
  className,
}: ProfileMobileHeaderProps) {
  const [bioExpanded, setBioExpanded] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <div
      className={cn(
        embeddedInHero ? "mt-5" : "pb-3",
        className
      )}
    >
      <div className="flex items-center gap-3">
        <Avatar
          className={cn(
            "shrink-0 border-2 border-white/15 shadow-md",
            embeddedInHero ? "h-14 w-14" : "h-12 w-12"
          )}
        >
          <AvatarImage src={avatarUrl} alt={displayName} />
          <AvatarFallback className="bg-dark-slate text-xl text-white">
            {nameInitial}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          <h2 className="truncate text-lg font-semibold leading-tight text-white">
            {displayName}
          </h2>
          <p className="mt-0.5 text-sm text-gray-light">Joined {joinDateText}</p>
        </div>

        <Button
          variant="outline"
          size="sm"
          className="h-9 shrink-0 gap-1.5 border-white/20 bg-white/5 px-3 text-primary hover:bg-primary hover:text-black"
          onClick={onEditProfile}
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </Button>
      </div>

      <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen} className="mt-3">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between rounded-lg bg-white/5 px-3 py-2.5 text-sm text-gray-light transition-colors hover:bg-white/10"
          >
            <span>Profile details</span>
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                detailsOpen && "rotate-180"
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2 space-y-3 rounded-lg bg-white/5 p-3">
          {bioText && (
            <div>
              <p className="text-sm leading-relaxed text-gray-light">
                {bioExpanded || bioText.length <= 200
                  ? bioText
                  : `${bioText.slice(0, 200)}...`}
              </p>
              {!bioExpanded && bioText.length > 200 && (
                <Button
                  variant="link"
                  className="h-auto p-0 text-primary"
                  onClick={() => setBioExpanded(true)}
                >
                  View more
                </Button>
              )}
            </div>
          )}

          <Button
            variant="outline"
            size="sm"
            className="h-10 w-full border-primary/30 text-primary hover:bg-primary hover:text-black"
            onClick={onManageSubscription}
          >
            Manage Subscription
          </Button>

          <div className="grid grid-cols-2 gap-2.5 text-sm">
            <div className="rounded-md border border-white/10 bg-dark-bg/40 px-3 py-2.5">
              <div className="text-xs text-gray-light">Workouts</div>
              <div className="mt-0.5 font-semibold text-white">
                {metricsLoading ? "—" : completedWorkouts.toLocaleString()}
              </div>
            </div>
            <div className="rounded-md border border-white/10 bg-dark-bg/40 px-3 py-2.5">
              <div className="text-xs text-gray-light">Points</div>
              <div className="mt-0.5 font-semibold text-primary">
                {metricsLoading ? "—" : totalPoints.toLocaleString()}
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
